import type { MembershipProject, ZotArtifact, ZotArtifactKind } from "@zworks/console-contracts";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, LoaderCircle, PanelRightClose, PanelRightOpen } from "lucide-react";
import * as React from "react";
import { EmptyState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { diffArtifactBody, type DiffLine } from "@/lib/artifact-diff";
import { cn } from "@/lib/utils";
import {
  ApiError,
  createZotArtifactProposal,
  zotArtifactQuery,
  zotArtifactVersionsQuery,
  zotArtifactsQuery,
} from "@/lib/api";

const kindLabels: Record<ZotArtifactKind, string> = {
  handoff: "Handoff",
  changeset: "Change set",
  draft: "Draft",
  report: "Report",
  diagram: "Diagram",
  export: "Export",
};

function artifactFilename(artifact: ZotArtifact): string {
  return `${artifact.artifactKey}-v${artifact.version}.md`;
}

/**
 * The Artifacts side panel beside the chat, as the Zot mockup shows it: an Artifacts
 * button with a count, one tab per artifact in the current chat, Copy and Download, and
 * footer actions that create proposals. Hiding the panel keeps every artefact: the list
 * reads stored versions, never panel state. Each artefact carries its version history,
 * so an earlier draft can be opened and compared with a later one.
 */
type ZotArtifactsPanelProps = {
  project: MembershipProject | null;
  threadId: string | null;
  open: boolean;
  onToggle: () => void;
  onProposal: () => void;
};

export function ZotArtifactsPanel(props: ZotArtifactsPanelProps) {
  return <ThreadArtifactsPanel key={`${props.project?.id}:${props.threadId}`} {...props} />;
}

function ThreadArtifactsPanel({
  project,
  threadId,
  open,
  onToggle,
  onProposal,
}: ZotArtifactsPanelProps) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actingIndex, setActingIndex] = React.useState<number | null>(null);
  const [fromVersion, setFromVersion] = React.useState<number | null>(null);
  const [toVersion, setToVersion] = React.useState<number | null>(null);

  const artifacts = useQuery({
    ...zotArtifactsQuery(project?.id ?? "", threadId),
    enabled: Boolean(project && threadId),
  });
  const list = React.useMemo(() => artifacts.data ?? [], [artifacts.data]);
  const selected = list.find((item) => item.id === selectedId) ?? list[0] ?? null;
  const detail = useQuery({
    ...zotArtifactQuery(project?.id ?? "", selected?.id ?? null),
    enabled: Boolean(project && selected),
  });
  const versions = useQuery({
    ...zotArtifactVersionsQuery(project?.id ?? "", threadId, selected?.artifactKey ?? null),
    enabled: Boolean(project && threadId && selected),
  });
  const history = React.useMemo(() => versions.data ?? [], [versions.data]);

  // The newest version is the one on show until the reader picks another pair.
  const newestVersion = history.length > 0 ? history[history.length - 1]!.version : null;
  const previousVersion = history.length > 1 ? history[history.length - 2]!.version : null;
  const from = fromVersion ?? previousVersion;
  const to = toVersion ?? newestVersion;
  const fromArtifact = history.find((item) => item.version === from) ?? null;
  const toArtifact = history.find((item) => item.version === to) ?? null;
  const shown = toArtifact ?? selected;
  const diff =
    fromArtifact && toArtifact
      ? diffArtifactBody(fromArtifact.bodyMarkdown, toArtifact.bodyMarkdown)
      : [];

  function selectArtifact(id: string) {
    setSelectedId(id);
    setActionError(null);
    setFromVersion(null);
    setToVersion(null);
  }

  async function copyBody() {
    if (!shown) return;
    try {
      await navigator.clipboard.writeText(shown.bodyMarkdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError("The artifact could not be copied.");
    }
  }

  function downloadBody() {
    if (!shown) return;
    const blob = new Blob([`${shown.bodyMarkdown}\n`], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = artifactFilename(shown);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  async function runAction(index: number) {
    if (!project || !shown || actingIndex !== null) return;
    setActingIndex(index);
    setActionError(null);
    try {
      await createZotArtifactProposal(project.id, shown.id, { actionIndex: index });
      await queryClient.invalidateQueries({ queryKey: ["projects", project.id, "zot"] });
      onProposal();
    } catch (cause) {
      setActionError(
        cause instanceof ApiError ? cause.message : "The proposal could not be created.",
      );
    } finally {
      setActingIndex(null);
    }
  }

  return (
    <div className="flex flex-col">
      <Button
        variant="outline"
        size="sm"
        onClick={onToggle}
        aria-pressed={open}
        aria-label={open ? "Hide artifacts" : "Show artifacts"}
      >
        {open ? (
          <PanelRightClose className="mr-1 size-4" aria-hidden />
        ) : (
          <PanelRightOpen className="mr-1 size-4" aria-hidden />
        )}
        Artifacts
        {list.length > 0 ? (
          <span
            className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-primary/10 px-1 text-xs font-medium"
            aria-label={`${list.length} artifacts`}
          >
            {list.length}
          </span>
        ) : null}
      </Button>
      {open ? (
        <section
          aria-label="Artifacts"
          className="mt-2 flex min-h-0 w-80 flex-col rounded-xl border bg-card"
        >
          {list.length > 1 ? (
            <div
              role="tablist"
              aria-label="Artifacts in this chat"
              className="flex gap-1 overflow-x-auto border-b p-2"
            >
              {list.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={(selected?.id ?? null) === item.id}
                  onClick={() => selectArtifact(item.id)}
                  className={cn(
                    "shrink-0 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent",
                    (selected?.id ?? null) === item.id && "bg-muted font-medium",
                  )}
                >
                  <span className="block max-w-40 truncate">{item.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {kindLabels[item.kind]} · v{item.version}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {!threadId ? (
            <div className="p-2">
              <EmptyState
                title="Artifacts"
                description="Documents and drafts Zot creates for you appear here once the chat starts."
              />
            </div>
          ) : artifacts.isPending ? (
            <p role="status" className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <LoaderCircle className="size-4 animate-spin" aria-hidden />
              Loading artifacts…
            </p>
          ) : artifacts.isError ? (
            <p role="alert" className="p-4 text-sm text-danger">
              Artifacts could not be loaded.{" "}
              <button type="button" className="underline" onClick={() => void artifacts.refetch()}>
                Try again
              </button>
            </p>
          ) : !selected ? (
            <div className="p-2">
              <EmptyState
                title="Nothing here yet"
                description="Documents, change sets, diagrams, and exports appear here as you work. Try /room to brainstorm a feature and get a handoff document."
              />
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2 border-b p-3">
                <div className="min-w-0">
                  <SectionHeading className="truncate">
                    {shown?.title ?? selected.title}
                  </SectionHeading>
                  <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge variant="secondary">{kindLabels[selected.kind]}</Badge>
                    <span>Version {shown?.version ?? selected.version}</span>
                    {detail.data && detail.data.versions.length > 1 ? (
                      <span aria-label={`${detail.data.versions.length} versions`}>
                        · v{detail.data.versions.join(", v")}
                      </span>
                    ) : null}
                    {selected.subtitle ? <span>· {selected.subtitle}</span> : null}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Copy artifact"
                    onClick={() => void copyBody()}
                  >
                    {copied ? (
                      <Check className="size-4" aria-hidden />
                    ) : (
                      <Copy className="size-4" aria-hidden />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Download artifact as Markdown"
                    onClick={downloadBody}
                  >
                    <Download className="size-4" aria-hidden />
                  </Button>
                </div>
              </div>
              <ArtifactVersions
                history={history}
                pending={versions.isPending}
                errored={versions.isError}
                onRetry={() => void versions.refetch()}
                from={from}
                to={to}
                onFromChange={setFromVersion}
                onToChange={setToVersion}
                diff={diff}
              />
              <div className="min-h-40 flex-1 overflow-y-auto p-3">
                <p className="whitespace-pre-wrap text-sm leading-6">
                  {shown?.bodyMarkdown ?? selected.bodyMarkdown}
                </p>
              </div>
              {shown && shown.actions.length > 0 ? (
                <div className="flex flex-wrap gap-2 border-t p-3">
                  {shown.actions.map((action, index) => (
                    <Button
                      key={`${index}-${action.label}`}
                      size="sm"
                      variant={index === 0 ? "default" : "outline"}
                      disabled={actingIndex !== null}
                      onClick={() => void runAction(index)}
                    >
                      {actingIndex === index ? (
                        <LoaderCircle className="mr-2 size-4 animate-spin" aria-hidden />
                      ) : null}
                      {action.label}
                    </Button>
                  ))}
                </div>
              ) : null}
              {actionError ? (
                <p role="alert" className="border-t p-3 text-sm text-danger">
                  {actionError}
                </p>
              ) : null}
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}

/**
 * The version picker and the diff between two versions of the same artefact. The reader
 * chooses which two drafts to compare; the diff names every line that changed.
 */
function ArtifactVersions({
  history,
  pending,
  errored,
  onRetry,
  from,
  to,
  onFromChange,
  onToChange,
  diff,
}: {
  history: ZotArtifact[];
  pending: boolean;
  errored: boolean;
  onRetry: () => void;
  from: number | null;
  to: number | null;
  onFromChange: (version: number) => void;
  onToChange: (version: number) => void;
  diff: DiffLine[];
}) {
  const [diffOpen, setDiffOpen] = React.useState(false);

  if (pending) {
    return (
      <p
        role="status"
        className="flex items-center gap-2 border-b px-3 py-2 text-xs text-muted-foreground"
      >
        <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
        Loading versions…
      </p>
    );
  }

  if (errored) {
    return (
      <p role="alert" className="border-b px-3 py-2 text-xs text-danger">
        Versions could not be loaded.{" "}
        <button type="button" className="underline" onClick={onRetry}>
          Try again
        </button>
      </p>
    );
  }

  if (history.length === 0) {
    return (
      <p className="border-b px-3 py-2 text-xs text-muted-foreground">
        No versions of this artefact are stored yet.
      </p>
    );
  }

  if (history.length === 1) {
    return (
      <p className="border-b px-3 py-2 text-xs text-muted-foreground">
        Only one version so far. A newer draft adds a diff here.
      </p>
    );
  }

  return (
    <section aria-label="Versions" className="border-b">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>From</span>
          <select
            aria-label="Compare from version"
            className="rounded-md border bg-background px-1.5 py-1 text-xs"
            value={from ?? ""}
            onChange={(event) => onFromChange(Number(event.target.value))}
          >
            {history.map((item) => (
              <option key={item.version} value={item.version}>
                v{item.version}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>To</span>
          <select
            aria-label="Compare to version"
            className="rounded-md border bg-background px-1.5 py-1 text-xs"
            value={to ?? ""}
            onChange={(event) => onToChange(Number(event.target.value))}
          >
            {history.map((item) => (
              <option key={item.version} value={item.version}>
                v{item.version}
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          aria-expanded={diffOpen}
          onClick={() => setDiffOpen((open) => !open)}
        >
          {diffOpen ? "Hide changes" : "Show changes"}
        </Button>
      </div>
      {diffOpen ? (
        <div className="max-h-48 overflow-y-auto border-t px-3 py-2">
          <p className="text-xs font-medium">
            v{from} to v{to}
          </p>
          <ArtifactDiff rows={diff} />
        </div>
      ) : null}
    </section>
  );
}

/** The diff rows: one line per changed or kept line, marked with its change. */
function ArtifactDiff({ rows }: { rows: DiffLine[] }) {
  if (rows.length === 0) {
    return (
      <p className="mt-1 text-xs text-muted-foreground">
        These two versions have the same content.
      </p>
    );
  }
  const changed = rows.filter((row) => row.change !== "unchanged").length;
  if (changed === 0) {
    return (
      <p className="mt-1 text-xs text-muted-foreground">
        These two versions have the same content.
      </p>
    );
  }
  return (
    <ul className="mt-1 flex flex-col gap-0.5">
      {rows.map((row, index) => (
        <li
          key={`${index}-${row.change}`}
          className={cn(
            "flex items-start gap-1.5 rounded px-1 py-0.5 font-mono text-xs leading-4",
            row.change === "added" && "bg-success/10 text-success",
            row.change === "removed" && "bg-danger/10 text-danger",
            row.change === "unchanged" && "text-muted-foreground",
          )}
        >
          <span aria-hidden className="w-3 shrink-0 select-none">
            {row.change === "added" ? "+" : row.change === "removed" ? "-" : " "}
          </span>
          <span className="min-w-0 break-words whitespace-pre-wrap">{row.text || " "}</span>
          <span className="sr-only">{row.change}</span>
        </li>
      ))}
    </ul>
  );
}
